/**
 * PDP swatch helpers.
 *
 * Metal is primary vs color: availability and click resolution ignore color, but
 * still honor size/length/carat/etc. Other attributes keep full combination matching.
 */

export const isColorAttributeId = (id) =>
    !!id && (id === 'color' || id.toLowerCase().includes('color'))

export const isLinkedPdpVariant = (product, variant) =>
    !!variant?.productId &&
    !!product?.c_allPdpLinks?.some(
        (p) =>
            p.id === variant.productId &&
            (p?.searchableIfUnavailableFlag || p?.comingSoonEligible || p?.orderable)
    )

/** Keep non-color variation values (and an optional key to always preserve, e.g. metal). */
export const withoutColorValues = (values = {}, preserveKey) =>
    Object.fromEntries(
        Object.entries(values).filter(
            ([key, val]) =>
                val != null && val !== '' && (key === preserveKey || !isColorAttributeId(key))
        )
    )

export const findLinkedVariantsMatching = (product, matchValues = {}) =>
    (product?.variants || []).filter(
        (candidate) =>
            Object.entries(matchValues).every(
                ([key, val]) => candidate?.variationValues?.[key] === val
            ) && isLinkedPdpVariant(product, candidate)
    )

export const pickPreferredVariant = (candidates = [], colorAttributeId, colorOrder = []) => {
    if (!candidates.length) return null

    const sorted = [...candidates].sort((a, b) => {
        const aIndex = colorOrder.indexOf(a.variationValues?.[colorAttributeId])
        const bIndex = colorOrder.indexOf(b.variationValues?.[colorAttributeId])
        return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex)
    })

    return sorted.find((candidate) => candidate.orderable) || sorted[0] || null
}

export const isMetalSwatchAvailable = (
    product,
    metalAttributeKey,
    metalValue,
    currentValues = {}
) => {
    const matchValues = withoutColorValues(
        {
            ...currentValues,
            [metalAttributeKey]: metalValue
        },
        metalAttributeKey
    )
    return findLinkedVariantsMatching(product, matchValues).length > 0
}

/**
 * Resolve the variant for a swatch click.
 * For metal: try exact combo first, then drop color and pick the preferred remaining variant.
 */
export const resolveSwatchVariant = ({
    product,
    attributeId,
    attributeKey,
    variationParams = {},
    currentValues = {}
}) => {
    const selectedVariationValues = {
        ...currentValues,
        ...variationParams
    }

    const exactMatches = findLinkedVariantsMatching(product, selectedVariationValues)
    if (exactMatches.length) {
        return exactMatches.find((candidate) => candidate.orderable) || exactMatches[0]
    }

    if (attributeId !== 'metal') {
        return null
    }

    const matchValues = withoutColorValues(
        {
            ...selectedVariationValues,
            [attributeKey]: variationParams[attributeKey]
        },
        attributeKey
    )
    const colorAttr = product?.variationAttributes?.find((attr) => isColorAttributeId(attr.id))
    const colorOrder = colorAttr?.values?.map((value) => value.value) || []

    return pickPreferredVariant(
        findLinkedVariantsMatching(product, matchValues),
        colorAttr?.id,
        colorOrder
    )
}
